import { CloseOutlined } from '@ant-design/icons'
import { User } from '@prisma/client'
import {
  Alert,
  Button,
  Divider,
  Flex,
  Form,
  Input,
  Radio,
  Switch,
  Typography,
} from 'antd'
import PhoneInput from 'antd-phone-input'
import TextArea from 'antd/es/input/TextArea'
import { CommonClient } from '~/agents/common/client'
import { Api } from '~/core/trpc'
import { AppSessionType } from '~/features/appSession/common'
import { ProviderAccountAuthorization } from '~/features/formConfiguration/client/components/CustomInputs/ProviderAccountAuthorization'
import { SelectProviderAccount } from '~/features/formConfiguration/client/components/CustomInputs/SelectProviderAccount'
import { SelectProviderAction } from '~/features/formConfiguration/client/components/CustomInputs/SelectProviderAction'
import { OnboardingValues } from '../../common'

type Props = {
  user: User
  appSession: AppSessionType
}

export const Settings: React.FC<Props> = ({ user, appSession }) => {
  const configuration = {
    appSessionId: appSession?.id,
    appId: appSession?.appId,
  }

  const values = (appSession?.values?.onboarding ?? {}) as OnboardingValues

  const valuesSafe = {
    ...values,
  } as OnboardingValues

  const retellAgentUpdateMutation = Api.retell.updateRetellAgent.useMutation()

  const logic = CommonClient.Form.useLogic<OnboardingValues>({
    appSession,
    cleanValues(values) {
      const valuesClean = { ...values }

      if (values.callTransfer?.mode === 'single') {
        const phoneNumber = values.callTransfer?.phoneNumber as any

        if (
          phoneNumber?.countryCode &&
          phoneNumber?.areaCode &&
          phoneNumber?.phoneNumber
        ) {
          valuesClean.callTransfer.phoneNumber = `+${phoneNumber.countryCode}${phoneNumber.areaCode}${phoneNumber.phoneNumber}`
        }
      }

      if (values.callTransfer?.mode === 'multiple') {
        values.callTransfer?.options.forEach((option, index) => {
          const phoneNumber = option.phoneNumber as any

          if (
            phoneNumber?.countryCode &&
            phoneNumber?.areaCode &&
            phoneNumber?.phoneNumber
          ) {
            valuesClean.callTransfer.options[index].phoneNumber =
              `+${phoneNumber.countryCode}${phoneNumber.areaCode}${phoneNumber.phoneNumber}`
          }
        })
      }

      return valuesClean
    },
  })

  const handleSubmit = async (valuesUpdated: OnboardingValues) => {
    await logic.submitOnboarding()

    await retellAgentUpdateMutation.mutateAsync({
      appSessionId: appSession.id,
    })
  }

  const isAgentDisabled = appSession.status === 'DISABLED'

  return (
    <>
      <CommonClient.Form.Wrapper
        {...logic}
        values={valuesSafe}
        onSubmit={handleSubmit}
      >
        <Typography.Title level={4} style={{ margin: 0 }}>
          Instructions
        </Typography.Title>
        <Divider className="my-3" />

        <CommonClient.Form.InputWrapper
          label="First greeting"
          description="What should I say when picking up the phone?"
          children={null}
        />
        <Form.Item
          name={['instructions', 'greeting']}
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>

        {!isAgentDisabled && (
          <>
            <CommonClient.Form.InputWrapper
              label="General instructions"
              description="How should I handle the conversation?"
              children={null}
            />
            <Form.Item
              name={['instructions', 'guidelines']}
              rules={[{ required: true }]}
            >
              <TextArea rows={10} maxLength={3000} />
            </Form.Item>
          </>
        )}

        <Typography.Title level={4} style={{ margin: 0 }}>
          Tools
        </Typography.Title>
        <Divider className="my-3" />

        {!isAgentDisabled && (
          <>
            <CommonClient.Form.InputWrapper
              label="End call conditions"
              description="Under which conditions should I end the call?"
              children={null}
            />
            <Form.List name={['instructions', 'endCallConditions']}>
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <Flex key={field.key} gap={1} style={{ width: '100%' }}>
                      <Form.Item
                        {...field}
                        name={[field.name]}
                        rules={[{ required: true, message: 'Required' }]}
                        style={{ flex: 1 }}
                      >
                        <Input placeholder={`Enter condition...`} />
                      </Form.Item>

                      {fields.length > 1 && (
                        <Button
                          type="text"
                          icon={<CloseOutlined />}
                          onClick={() => remove(field.name)}
                        />
                      )}
                    </Flex>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      disabled={fields.length >= 5}
                    >
                      Add Condition
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </>
        )}

        <CommonClient.Form.InputWrapper
          label="Call Transfer"
          description="How should I transfer the call?"
          children={null}
        />

        <Form.Item shouldUpdate={true}>
          {options => {
            const isActive = options.getFieldValue([
              'callTransfer',
              'activated',
            ])

            if (!isActive) {
              return <></>
            }

            const mode = options.getFieldValue(['callTransfer', 'mode'])

            return (
              <>
                <Form.Item name={['callTransfer', 'mode']}>
                  <Radio.Group
                    block={true}
                    optionType="button"
                    buttonStyle="solid"
                    options={[
                      {
                        label: 'Single',
                        value: 'single',
                      },
                      {
                        label: 'Multiple',
                        value: 'multiple',
                      },
                    ]}
                  />
                </Form.Item>

                {mode === 'single' && (
                  <>
                    <CommonClient.Form.InputWrapper children={null} />
                    <Form.Item
                      name={['callTransfer', 'phoneNumber']}
                      getValueFromEvent={value => {
                        if (
                          value.countryCode &&
                          value.areaCode &&
                          value.phoneNumber
                        ) {
                          return `+${value.countryCode}${value.areaCode}${value.phoneNumber}`
                        }

                        return null
                      }}
                      rules={[
                        {
                          required: true,
                        },
                        {
                          pattern: new RegExp('^\\+[1-9]\\d{1,14}$'),
                          message: `Invalid format.`,
                        },
                      ]}
                    >
                      <PhoneInput
                        enableSearch
                        disableParentheses
                        enableArrow
                        distinct
                        onlyCountries={['us']}
                      />
                    </Form.Item>
                  </>
                )}

                {mode === 'multiple' && (
                  <Form.List name={['callTransfer', 'options']}>
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map((field, index) => (
                          <Flex
                            key={field.key}
                            gap={1}
                            style={{ width: '100%' }}
                          >
                            <Form.Item
                              {...field}
                              style={{ flex: 1 }}
                              name={[field.name, 'phoneNumber']}
                              fieldKey={[field.fieldKey, 'phoneNumber']}
                              getValueFromEvent={value => {
                                if (
                                  value.countryCode &&
                                  value.areaCode &&
                                  value.phoneNumber
                                ) {
                                  return `+${value.countryCode}${value.areaCode}${value.phoneNumber}`
                                }
                                return null
                              }}
                              rules={[
                                { required: true },
                                {
                                  pattern: new RegExp('^\\+[1-9]\\d{1,14}$'),
                                  message: 'Invalid format.',
                                },
                              ]}
                            >
                              <PhoneInput
                                enableSearch
                                disableParentheses
                                enableArrow
                                distinct
                                onlyCountries={['us']}
                              />
                            </Form.Item>

                            <Form.Item
                              {...field}
                              style={{ flex: 1 }}
                              name={[field.name, 'condition']}
                              fieldKey={[field.fieldKey, 'condition']}
                              rules={[{ required: true }]}
                            >
                              <Input placeholder="Condition" />
                            </Form.Item>

                            {fields.length > 1 && (
                              <Button
                                type="text"
                                icon={<CloseOutlined />}
                                onClick={() => remove(field.name)}
                              />
                            )}
                          </Flex>
                        ))}

                        <Form.Item>
                          <Button
                            type="dashed"
                            onClick={() => add()}
                            block
                            disabled={fields.length >= 5}
                          >
                            Add Phone Number
                          </Button>
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                )}
              </>
            )
          }}
        </Form.Item>

        {!isAgentDisabled && (
          <>
            <CommonClient.Form.InputWrapper
              label="Calendar"
              description="Should I book appointments on your calendar?"
            >
              <Form.Item name={['calendarServices', 'activated']}>
                <Switch size="small" />
              </Form.Item>
            </CommonClient.Form.InputWrapper>

            {/* <Typography.Title level={4} style={{ margin: 0, marginTop: 32 }}>
            Calendar
          </Typography.Title> */}

            {/* <Divider className="my-3" /> */}

            <Form.Item shouldUpdate={true}>
              {options => {
                const isActive = options.getFieldValue([
                  'calendarServices',
                  'activated',
                ])

                if (!isActive) {
                  return <></>
                }

                return (
                  <>
                    <Form.Item name={['calendarServices', 'provider']}>
                      <Radio.Group
                        block={true}
                        optionType="button"
                        buttonStyle="solid"
                        options={[
                          {
                            label: 'Google',
                            value: 'googleCalendar',
                          },
                          {
                            label: 'Outlook',
                            value: 'outlookCalendar',
                          },
                        ]}
                      />
                    </Form.Item>

                    <Form.Item<OnboardingValues> shouldUpdate={true}>
                      {instance => {
                        const provider = instance.getFieldValue([
                          'calendarServices',
                          'provider',
                        ])

                        if (provider === 'googleCalendar') {
                          return (
                            <CommonClient.Form.InputWrapper label="Connect your Google Calendar account">
                              <Form.Item
                                name={['calendarServices', 'providerAccountId']}
                              >
                                <SelectProviderAccount providerKey="googleCalendar" />
                              </Form.Item>
                            </CommonClient.Form.InputWrapper>
                          )
                        }

                        return (
                          <CommonClient.Form.InputWrapper label="Connect your Outlook Calendar account">
                            <Form.Item
                              name={['calendarServices', 'providerAccountId']}
                            >
                              <SelectProviderAccount providerKey="outlookCalendar" />
                            </Form.Item>
                          </CommonClient.Form.InputWrapper>
                        )
                      }}
                    </Form.Item>

                    <Form.Item<OnboardingValues> shouldUpdate={true}>
                      {instance => {
                        const provider = instance.getFieldValue([
                          'calendarServices',
                          'provider',
                        ])

                        const providerAccountId = instance.getFieldValue([
                          'calendarServices',
                          'providerAccountId',
                        ])

                        if (
                          !providerAccountId ||
                          provider !== 'googleCalendar'
                        ) {
                          return <></>
                        }

                        return (
                          <Form.Item
                            name={['googleCalendarSettings', 'permissionBook']}
                            rules={[
                              {
                                validator: (_, value) =>
                                  value?.[0]?.isCorrupted
                                    ? Promise.reject()
                                    : Promise.resolve(),
                                message: 'Authorization is not valid',
                              },
                            ]}
                          >
                            <ProviderAccountAuthorization
                              providerAccountId={providerAccountId}
                              scope="https://www.googleapis.com/auth/calendar"
                              ui={{
                                title: 'Manage Calendar',
                                iconAntd: 'GoogleOutlined',
                                description:
                                  'Allow me to book events on your calendar',
                              }}
                            />
                          </Form.Item>
                        )
                      }}
                    </Form.Item>

                    <Form.Item<OnboardingValues> shouldUpdate={true}>
                      {options => {
                        const provider = options.getFieldValue([
                          'calendarServices',
                          'provider',
                        ])

                        const providerAccountId = options.getFieldValue([
                          'calendarServices',
                          'providerAccountId',
                        ])

                        const permission = options.getFieldValue([
                          'googleCalendarSettings',
                          'permissionBook',
                        ])

                        if (
                          !permission ||
                          !providerAccountId ||
                          provider !== 'googleCalendar'
                        ) {
                          return <></>
                        }

                        return (
                          <Form.Item
                            name={['googleCalendarSettings', 'calendarId']}
                            label="Pick your calendar"
                            rules={[
                              {
                                required: true,
                                message: 'Please choose a calendar to continue',
                              },
                            ]}
                          >
                            <SelectProviderAction
                              providerKey="googleCalendar"
                              providerItemsKey="calendars"
                              providerActionKey="findManyCalendars"
                              providerItemLabel="{item.name}"
                              providerAccountId={providerAccountId}
                              providerAccountScopeValues={permission}
                              providerItemValueKey="id"
                              formValuesConfiguration={configuration}
                            />
                          </Form.Item>
                        )
                      }}
                    </Form.Item>
                  </>
                )
              }}
            </Form.Item>
          </>
        )}

        {isAgentDisabled && (
          <Alert
            type="warning"
            description="While Rachel is on a break, she will transfer the call directly."
          />
        )}
      </CommonClient.Form.Wrapper>
    </>
  )
}
